import type { PlatformProvider } from '@/api/schemasPlatformProviders';
import { Icon } from '@/components/Icon';
import { TONE_PILL } from '@/components/badges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { PROVIDER_COPY, actionLabel, lastSetText, lastSetTitle, statusView } from './platformProvidersModel';
import { WhereToGetIt } from './WhereToGetIt';

interface Props {
  provider: PlatformProvider;
  /** False when the API reads the tokens from its environment: no action is offered. */
  editable: boolean;
  now: number;
  onSet: (provider: PlatformProvider) => void;
}

/** One of Malible's provider tokens: its status, the account it belongs to, when and by whom it was set, and the action. */
export function PlatformProviderCard({ provider, editable, now, onSet }: Props) {
  const copy = PROVIDER_COPY[provider.provider];
  const status = statusView(provider);
  const lastSet = lastSetText(provider, now);
  return (
    <Card data-testid={`platform-provider-${provider.provider}`} className="enter flex min-w-0 flex-col gap-4 p-4 sm:p-5">
      <div className="flex min-w-0 items-start gap-3">
        <span aria-hidden className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg border bg-card font-mono text-[11px] font-semibold', provider.provider === 'bitrise' ? 'text-bitrise' : 'text-foreground')}>{copy.mark}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-semibold text-foreground">{copy.name}</h2>
            <Badge variant="outline" data-testid={`platform-provider-status-${provider.provider}`} className={cn('gap-1.5 font-mono text-[11px] font-normal leading-4', TONE_PILL[status.tone])}>
              <Icon name={status.tone === 'succeeded' ? 'check_circle' : 'warning'} size={14} />
              {status.label}
            </Badge>
          </div>
          <p data-testid={`platform-provider-account-${provider.provider}`} className="mt-0.5 break-words text-[13px] text-muted-foreground">{status.detail}</p>
          {lastSet ? <p title={lastSetTitle(provider)} className="mt-0.5 text-xs text-subtle">{lastSet}</p> : null}
        </div>
      </div>
      <p className="text-[13px] text-muted-foreground">{copy.purpose}</p>
      <WhereToGetIt kind={provider.provider} />
      {editable ? (
        <div className="mt-auto flex justify-end border-t pt-4">
          <Button variant="outline" data-testid={`platform-provider-set-${provider.provider}`} className="max-sm:w-full" onClick={() => onSet(provider)}>
            <Icon name="key" size={18} />
            {actionLabel(provider)}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
